import { useCallback } from "react";
import { useCuepointsStore } from "./useCuepointsStore";
import { Highlight, useHighlightsStore } from "./useHighlightsStore";
import { postToIframe } from "./utils";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";

export const useRemoveHighlight = (projectId: string) => {
  const { trackEvent } = useAnalytics();
  const { cuepoints, deleteCuepoint, setCurrentCuepoint, currentCuepoint } =
    useCuepointsStore();

  const {
    deleteHighlight,
    deleteSectionHighlight,
    sectionHighlights,
    highlightedStoryboard,
    setHighlightedStoryboard,
    setCurrentHighlight,
    currentHighlight,
  } = useHighlightsStore();

  const removeCuepointsFromHighlight = useCallback(
    (highlightId?: string) => {
      const hasSource = cuepoints?.some((c) => "sourceId" in c);

      cuepoints.forEach((cue) => {
        const idToCheck = hasSource ? cue?.sourceId : cue?.highlightId;
        if (idToCheck === highlightId) {
          deleteCuepoint(highlightId, cue.start);
          setCurrentCuepoint(undefined);
        }
      });

      // Derive from sectionHighlights so script/store cleanup runs even when
      // the cuepoint hasn't landed yet (race after adding a highlight).
      const sectionHighlight = sectionHighlights?.find(
        (h) => h.sourceId === highlightId
      );
      if (sectionHighlight) {
        if (highlightedStoryboard?.sections?.length) {
          const newSections = highlightedStoryboard.sections.map((s) => {
            if (s.id === sectionHighlight.sectionId) {
              return {
                ...s,
                script: {
                  ...s.script,
                  content: s.script?.content
                    ?.replaceAll(`[[HL-${sectionHighlight?.id}]]`, "")
                    .replaceAll(`[[/HL-${sectionHighlight?.id}]]`, ""),
                },
              };
            }
            return s;
          });
          setHighlightedStoryboard({
            ...highlightedStoryboard,
            sections: newSections,
          });
        }
        deleteSectionHighlight(highlightId);
      }
    },
    [
      cuepoints,
      deleteCuepoint,
      setCurrentCuepoint,
      sectionHighlights,
      highlightedStoryboard,
      setHighlightedStoryboard,
      deleteSectionHighlight,
    ]
  );

  const removeHighlight = useCallback(
    (
      highlight: Highlight,
      options: {
        trackAnalytics?: boolean;
        postToIframe?: boolean;
        clearCurrentHighlight?: boolean;
      } = {}
    ) => {
      const {
        trackAnalytics = true,
        postToIframe: shouldPostToIframe = true,
        clearCurrentHighlight = true,
      } = options;

      if (!highlight) return;

      removeCuepointsFromHighlight(highlight.id);
      deleteHighlight(highlight.id);

      if (trackAnalytics && currentCuepoint) {
        trackEvent(AnalyticsEvents.DocumentAnimationHighlightDelete, {
          project_id: projectId,
          highlight_type: currentCuepoint.roughNotation.type,
          highlight_source: currentHighlight?.generated ? "system" : "user",
          document_format: highlight.type,
        });
      }

      if (clearCurrentHighlight) {
        setCurrentHighlight(undefined);
      }

      if (shouldPostToIframe) {
        postToIframe({
          type: "highlight-deleted",
          id: highlight.id,
        });

        // Send reindex message after deletion
        postToIframe({
          type: "reindex",
        });
      }
    },
    [
      removeCuepointsFromHighlight,
      deleteHighlight,
      trackEvent,
      projectId,
      currentCuepoint,
      currentHighlight,
      setCurrentHighlight,
    ]
  );

  return {
    removeHighlight,
    removeCuepointsFromHighlight,
  };
};
